import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Users, DollarSign, BarChart3, Sparkles, LogIn } from 'lucide-react';
import { trackPLGEvent } from '@/hooks/usePLGAnalytics';

export type GateSurface = 'alunos' | 'financeiro' | 'gestao' | 'default';

interface Copy {
  eyebrow: string;
  headline: string;
  sub: string;
  cta: string;
  ghost: 'alunos' | 'financeiro' | 'gestao';
}

const COPY: Record<GateSurface, Copy> = {
  alunos: {
    eyebrow: 'Alunos · gestão do tatame',
    headline: 'Salve seus alunos. Sagat cobra por você.',
    sub: 'Cadastro em 30 segundos. Sagat manda a cobrança pelo Zap, você fica no tatame.',
    cta: 'Ativar meu sócio invisível',
    ghost: 'alunos',
  },
  financeiro: {
    eyebrow: 'Financeiro · pulso do CT',
    headline: 'Veja quem deve, sem abrir planilha.',
    sub: 'MRR, atrasos e recebidos numa tela. Sagat cobra pelo Zap sem você digitar uma palavra.',
    cta: 'Ativar meu sócio invisível',
    ghost: 'financeiro',
  },
  gestao: {
    eyebrow: 'Gestão · visão de Mestre',
    headline: 'Sagat te mostra quem tá sumindo — antes de sumir.',
    sub: 'Radar de churn, resgate por WhatsApp e histórico do tatame. Tudo automático.',
    cta: 'Ativar meu sócio invisível',
    ghost: 'gestao',
  },
  default: {
    eyebrow: 'FalaCoSagat',
    headline: 'Crie sua conta em 10 segundos.',
    sub: 'Seu WhatsApp + PIN de 6 dígitos. Sem SMS, sem senha longa.',
    cta: 'Criar conta grátis',
    ghost: 'alunos',
  },
};

function GhostAlunos() {
  return (
    <div className="space-y-2">
      {[
        { name: 'João da Silva', plan: 'Mensal · R$ 180', day: 'Dia 5', color: 'bg-emerald-500/70' },
        { name: 'Maria Souza', plan: 'Trimestral · R$ 480', day: 'Dia 12', color: 'bg-amber-500/70' },
        { name: 'Ricardo Lima', plan: 'Mensal · R$ 200', day: 'Dia 20', color: 'bg-rose-500/70' },
      ].map((s) => (
        <div
          key={s.name}
          className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3"
        >
          <div className={`h-9 w-9 rounded-full ${s.color} flex items-center justify-center text-white text-xs font-bold`}>
            {s.name.split(' ').map((w) => w[0]).join('').slice(0, 2)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-ink-hi truncate">{s.name}</p>
            <p className="text-xs text-ink-mid truncate">{s.plan}</p>
          </div>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-low">{s.day}</span>
        </div>
      ))}
    </div>
  );
}

function GhostFinanceiro() {
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-thai-red/10 to-transparent p-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-low">MRR</p>
        <p className="mt-1 font-sora text-3xl font-bold text-ink-hi tabular-nums">R$ 4.820</p>
        <p className="mt-1 text-xs text-emerald-400">+R$ 180 hoje</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
          <p className="text-[10px] uppercase tracking-wider text-ink-low">Recebido</p>
          <p className="text-lg font-bold text-emerald-400 tabular-nums">R$ 3.200</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
          <p className="text-[10px] uppercase tracking-wider text-ink-low">Atrasado</p>
          <p className="text-lg font-bold text-thai-red tabular-nums">R$ 620</p>
        </div>
      </div>
    </div>
  );
}

function GhostGestao() {
  return (
    <div className="space-y-2">
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-thai-red">Risco de evasão</p>
        <p className="text-sm font-semibold text-ink-hi mt-1">3 alunos sem treinar há 7+ dias</p>
      </div>
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-400">Sagat em ação</p>
        <p className="text-sm text-ink-mid mt-1">12 mensagens enviadas · 8 confirmações</p>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: 21 }).map((_, i) => (
          <div
            key={i}
            className="aspect-square rounded-sm"
            style={{
              background: `hsl(0 0% ${20 + ((i * 37) % 50)}%)`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

const GHOST_COMPONENTS = {
  alunos: GhostAlunos,
  financeiro: GhostFinanceiro,
  gestao: GhostGestao,
};

const SURFACE_ICONS = {
  alunos: Users,
  financeiro: DollarSign,
  gestao: BarChart3,
  default: Sparkles,
};

interface Props {
  surface: GateSurface;
}

export const ContextualGate = ({ surface }: Props) => {
  const navigate = useNavigate();
  const copy = COPY[surface];
  const Ghost = GHOST_COMPONENTS[copy.ghost];
  const Icon = SURFACE_ICONS[surface];

  useEffect(() => {
    trackPLGEvent('contextual_gate_shown', { surface });
  }, [surface]);

  const goSignup = () => {
    trackPLGEvent('contextual_gate_cta_click', { surface, action: 'signup' });
    navigate('/app/auth', { state: { mode: 'signup', from: surface } });
  };

  const goLogin = () => {
    trackPLGEvent('contextual_gate_cta_click', { surface, action: 'login' });
    navigate('/app/auth', { state: { mode: 'login', from: surface } });
  };

  return (
    <div className="relative min-h-screen bg-canvas overflow-hidden pb-nav-safe">
      {/* Ghost preview blurred no fundo */}
      <div className="absolute inset-0 px-4 pt-8 pointer-events-none select-none">
        <div className="mx-auto max-w-md space-y-3 blur-[6px] opacity-50">
          <Ghost />
          <Ghost />
        </div>
      </div>

      {/* Overlay escuro pra legibilidade */}
      <div className="absolute inset-0 bg-gradient-to-b from-canvas/40 via-canvas/85 to-canvas" />

      {/* CTA card */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-surface-1/95 backdrop-blur-xl p-6 shadow-2xl">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-thai-red/15 border border-thai-red/30 flex items-center justify-center">
            <Icon className="h-6 w-6 text-thai-red" />
          </div>
          <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.22em] text-thai-red text-center">
            {copy.eyebrow}
          </p>
          <h1 className="mt-2 font-sora text-2xl font-bold text-ink-hi text-center tracking-tight">
            {copy.headline}
          </h1>
          <p className="mt-3 text-sm text-ink-mid text-center leading-relaxed">
            {copy.sub}
          </p>
          <div className="mt-6 space-y-2">
            <Button onClick={goSignup} size="lg" className="w-full h-12 bg-thai-red hover:bg-thai-red/90 text-white font-semibold">
              {copy.cta}
            </Button>
            <Button onClick={goLogin} variant="ghost" size="sm" className="w-full text-ink-mid">
              <LogIn className="mr-2 h-4 w-4" />
              Já tenho conta
            </Button>
          </div>
          <p className="mt-4 text-[11px] text-ink-low text-center">
            10 segundos · seu WhatsApp + PIN · sem cartão
          </p>
        </div>
      </div>
    </div>
  );
};